import { useState } from 'react';
import { Button, Input, Modal, ModalContent } from '@strivelabs/ui-core/v2';
import type { WorkflowExecution } from '@/hooks/useWorkflowExecutions';
import { CHAT_TITLE_MAX_LENGTH } from '../../hooks/useChatActions';
import { titleOf } from '../../hooks/useChatList';

export interface RenameDialogProps {
  execution: WorkflowExecution | null;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (title: string) => void;
}

interface RenameFormProps {
  execution: WorkflowExecution;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (title: string) => void;
}

/**
 * The form itself, seeded once per conversation.
 *
 * Keyed on the execution id by its parent so opening a different conversation
 * remounts it with a fresh draft. That replaces an effect that copied the title
 * into state on every change of `execution` — derived state that had to be kept
 * in sync by hand, and briefly rendered the previous chat's title.
 */
function RenameForm({ execution, isSaving, onCancel, onSave }: RenameFormProps) {
  const [draft, setDraft] = useState(execution.title ?? '');

  return (
    <Modal open onOpenChange={next => !next && onCancel()}>
      <ModalContent className="w-[420px] p-5" ariaLabel="Rename chat">
        <form
          onSubmit={event => {
            event.preventDefault();
            onSave(draft);
          }}
        >
          <h2 className="text-v2-lg font-semibold text-v2-foreground">Rename chat</h2>
          <p className="mt-1 text-v2-sm text-v2-muted-foreground">
            Leave this empty to restore the automatic title.
          </p>

          <Input
            value={draft}
            onChange={setDraft}
            maxLength={CHAT_TITLE_MAX_LENGTH}
            autoFocus
            aria-label="Chat title"
            placeholder={titleOf(execution)}
            className="mt-4"
          />

          <div className="mt-5 flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" size="sm" state={isSaving ? 'loading' : undefined}>
              Save
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
}

/** Rename a conversation. Submitting an empty value clears back to the derived title. */
export function RenameDialog({ execution, isSaving, onCancel, onSave }: RenameDialogProps) {
  if (!execution) return null;

  return (
    <RenameForm
      key={execution.id}
      execution={execution}
      isSaving={isSaving}
      onCancel={onCancel}
      onSave={onSave}
    />
  );
}
